import { Component, Input, OnInit, ChangeDetectionStrategy, inject } from "@angular/core";
import { LiveAnnouncer } from "@angular/cdk/a11y";
import { TranslateService } from "@ngx-translate/core";
import { NgbActiveModal } from "@ng-bootstrap/ng-bootstrap";

import { BaseComponent, UserService, communitiesHubEnabled } from "@mypxplat/xplat/core";
import { AppService, CommunityChannelPrefs, CommunityTopicPrefs, WebCommunityService } from "@mypxplat/xplat/web/core";

/**
 * Community settings modal, in one of two modes:
 *
 *   - **All communities** (no `communityStringId`): the member's defaults, saved to
 *     `user_profiles/{uid}.communityDefaults`. Opened from the hub's gear.
 *   - **One community**: what this community uses, saved as OVERRIDES of those defaults —
 *     only what differs is stored (`saveCommunityOverrides`), and "Use my defaults" drops them.
 *
 * Signature and feed layout are global in both modes. The rest of this note predates the
 * defaults and describes the per-community storage the overrides still use.
 *
 * Per-community settings modal. Opened from the gear button on the community detail
 * header and from the gear on each card in the `/communities` grid. Everything a member
 * can tune about ONE community lives here; it started as notification preferences only
 * (DENG-733) and was widened in DENG-847, so expect more sections over time.
 *
 * Three maps, all keyed by community `string_id`, all written together on Save:
 *
 *   - Channel prefs (email / mobile / desktop) →
 *     `notificationPreferences.communities[stringId]`
 *   - Topic prefs (which events even generate a notification) →
 *     `communityNotificationPreferences[stringId]`
 *   - Signature (appended to posts in this community) →
 *     `communitySignatures[stringId]`, or `defaultCommunitySignature` when the member
 *     uses it in all their communities
 *
 * Notification defaults come from `WebCommunityService.getCommunity*Prefs` — they must
 * stay in lockstep with the paeapi `notificationTopicPref.ts` DEFAULT_ALLOWED map or the
 * UI shows a state the server doesn't actually enforce. The modal closes only once every
 * write resolves.
 */
@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: "myp-community-settings",
  templateUrl: "community-settings.component.html",
  styleUrls: ["community-settings.component.scss"],
  standalone: false,
})
export class CommunitySettingsComponent extends BaseComponent implements OnInit {
  /** Absent = settings for all communities. */
  @Input() communityStringId?: string | null;
  @Input() communityTitle?: string;

  public channelPrefs: CommunityChannelPrefs = { email: true, mobile: true, desktop: true };
  public topicPrefs: CommunityTopicPrefs = { new_posts: false, replies_to_me: true, replies_in_my_threads: true, mentions: true, reactions_on_my_posts: true };
  /**
   * Signature for this community (DENG-847). Plain text, appended to the end of posts the
   * member writes here — the composer shows it and lets them drop it per post.
   */
  public signature = "";
  /**
   * Save the signature as the member's all-communities default instead of this community's
   * own. On by default when this community has no signature of its own, so setting one
   * anywhere sets it everywhere; switch it off to give this community a different one.
   */
  public signatureForAllCommunities = true;
  /**
   * Feed ordering for this community. A switch rather than two radios: there are exactly two
   * orderings and one of them is the default, so "on" reads as the preference and "off" as
   * the alternative.
   */
  public sortByRecentActivity = true;
  /** All communities only: the hub opens on Favorite Channels (on) or All Channels (off). */
  public startOnFavorites = true;
  /**
   * The "Default feed" switch: it picks where the hub opens, so it is only offered to viewers
   * who get the hub (`communitiesHubEnabled`). Hidden otherwise, and `startView` is left as stored.
   */
  public showStartView = false;
  /** Guard rail, not a server-enforced limit: a signature is a sign-off, not a second post. */
  public readonly signatureMaxLength = 200;
  public saving = false;
  public unfollowing = false;
  /** Whether the user currently follows this community. Drives the Unfollow-button visibility. */
  public isFollowing = false;
  /** Editing the defaults for every community rather than one community. */
  public allCommunities = false;
  /** This community has settings of its own (one-community mode only). */
  public customized = false;
  public resetting = false;
  /** "Saved", spoken: the modal closes on save, so nothing on screen says it worked. */
  private _announcer = inject(LiveAnnouncer);
  private _translate = inject(TranslateService);

  constructor(
    userService: UserService,
    public activeModal: NgbActiveModal,
    public appService: AppService,
    public communityService: WebCommunityService
  ) {
    super(userService);
  }

  ngOnInit() {
    this.allCommunities = !this.communityStringId;
    this.showStartView = communitiesHubEnabled(this.userService.checkEmployeeAccess(this.userService.user));
    this.load();
  }

  /**
   * Current values (or defaults) into local state, so toggles bind against a copy — partial
   * edits must not leak into the cached profile until Save. Cancel just discards these.
   */
  load() {
    if (this.allCommunities) {
      const defaults = this.communityService.getCommunityDefaults();
      this.channelPrefs = { ...defaults.channels };
      this.topicPrefs = { ...defaults.topics };
      this.signature = this.communityService.getDefaultCommunitySignature();
      this.signatureForAllCommunities = true;
      this.sortByRecentActivity = defaults.sortMode === "recentactivity";
      this.startOnFavorites = defaults.startView === "favorites";
      this.isFollowing = false;
      this.customized = false;
      return;
    }
    const id = this.communityStringId as string;
    this.channelPrefs = this.communityService.getCommunityChannelPrefs(id);
    this.topicPrefs = this.communityService.getCommunityTopicPrefs(id);
    this.isFollowing = this.communityService.isFollowingCommunity(id);
    this.signature = this.communityService.getCommunitySignature(id);
    this.signatureForAllCommunities = !this.communityService.getOwnCommunitySignature(id);
    this.sortByRecentActivity = this.communityService.getCommunitySortMode(id) === "recentactivity";
    this.customized = this.communityService.hasCommunityOverrides(id);
  }

  /** From one community's settings to everyone's, in place. Unsaved edits are dropped. */
  editAllCommunities() {
    this.allCommunities = true;
    this.load();
  }

  /** Drop this community's overrides so it follows the member's defaults again. */
  useDefaults() {
    if (this.resetting || !this.communityStringId) return;
    this.resetting = true;
    this.communityService
      .resetCommunitySettings(this.communityStringId)
      .then(() => {
        this.resetting = false;
        this.load();
      })
      .catch((err) => {
        this.resetting = false;
        console.error("[community-settings] reset failed", err);
        this.appService.alert("Sorry, we couldn't reset these settings. Please try again.");
      });
  }

  save() {
    if (this.saving) return;
    this.saving = true;
    const sortMode: "recentactivity" | "date" = this.sortByRecentActivity ? "recentactivity" : "date";
    const writes = this.allCommunities
      ? [
          this.communityService.updateCommunityDefaults({
            channels: { ...this.channelPrefs },
            topics: { ...this.topicPrefs },
            sortMode,
            ...(this.showStartView ? { startView: this.startOnFavorites ? ("favorites" as const) : ("all" as const) } : {}),
          }),
          this.communityService.updateDefaultCommunitySignature(this.signature),
        ]
      : [this.communityService.saveCommunityOverrides(this.communityStringId as string, { channels: this.channelPrefs, topics: this.topicPrefs, sortMode }), this.saveSignature()];
    Promise.all(writes)
      .then(() => {
        this.saving = false;
        this.activeModal.close("saved");
        this._announcer.announce(this._translate.instant("general.saved"), "polite");
      })
      .catch((err) => {
        this.saving = false;
        console.error("[community-settings] save failed", err);
        this.appService.alert("Sorry, we couldn't save your settings. Please try again.");
      });
  }

  /**
   * All communities: write the default and drop this community's own signature so it
   * follows the default from now on. This community only: write its own; saved blank, it
   * falls back to the default again.
   */
  private saveSignature(): Promise<any> {
    const id = this.communityStringId as string;
    if (!this.signatureForAllCommunities) return this.communityService.updateCommunitySignature(id, this.signature);
    return Promise.all([
      this.communityService.updateDefaultCommunitySignature(this.signature),
      this.communityService.getOwnCommunitySignature(id) ? this.communityService.updateCommunitySignature(id, "") : Promise.resolve(),
    ]);
  }

  /**
   * Unfollow the community from within this modal. Doesn't touch the pref maps — those
   * stay put in case the user re-follows later (their previous prefs then take effect
   * again). Closes the modal with a distinct "unfollowed" reason so callers that care
   * (e.g. the /communities grid) could react — but at time of writing everyone just
   * observes `firebaseProfile$` and re-derives state.
   */
  unfollow() {
    if (this.unfollowing || !this.communityStringId) return;
    this.unfollowing = true;
    this.communityService
      .unfollowCommunity(this.communityStringId)
      .then(() => {
        this.unfollowing = false;
        this.isFollowing = false;
        this.activeModal.close("unfollowed");
        this._announcer.announce(this._translate.instant("communities_hub.left_named", { name: this.communityTitle || "" }), "polite");
      })
      .catch((err) => {
        this.unfollowing = false;
        console.error("[community-settings] unfollow failed", err);
        this.appService.alert("Sorry, we couldn't remove you from this community. Please try again.");
      });
  }
}
